import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { ToastService } from '../../notifications/services/toast.service';
import { environment } from '../../../../environments/environment';

interface BackendError {
  message?: string;
  error?: string;
  detail?: string;
  errors?: string[];
  errorCode?: string;
}

interface ExtractedError {
  message: string;
  errorCode?: string;
}

const API_BASE_URL = environment.apiUrl.replace(/\/$/, '');
const API_PATTERNS = [/^\/api\//, new RegExp(`^${escapeRegExp(API_BASE_URL)}(?:/|$)`)];
const AUTH_PATTERNS = [/\/auth\//];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  const isApiRequest = API_PATTERNS.some((pattern) => pattern.test(req.url));

  if (!isApiRequest) {
    return next(req);
  }

  let apiReq = req.clone({ withCredentials: true });

  const memoryToken = authService.getAccessToken();
  if (memoryToken) {
    apiReq = apiReq.clone({
      setHeaders: {
        Authorization: `Bearer ${memoryToken}`,
      },
    });
  }

  return next(apiReq);
};

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const tokenRefreshService = inject(TokenRefreshService);
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const extracted = extractBackendErrorMessage(error);

      const isAuthEndpoint = AUTH_PATTERNS.some((pattern) => pattern.test(req.url));
      if (error.status === 401 && !isAuthEndpoint) {
        return from(tokenRefreshService.refreshIfNeeded()).pipe(
          switchMap((success) => {
            if (success) {
              let authReq = req.clone({ withCredentials: true });
              const newToken = authService.getAccessToken();
              if (newToken) {
                authReq = authReq.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                  },
                });
              }
              return next(authReq);
            }
            void authService.handleSessionExpired();
            return throwError(() => createHttpError(error, extracted));
          }),
          catchError(() => {
            void authService.handleSessionExpired();
            return throwError(() => createHttpError(error, extracted));
          }),
        );
      }

      if (shouldShowToastForRequest(req.url, error.status)) {
        toastService.error(extracted.message);
      }

      return throwError(() => createHttpError(error, extracted));
    }),
  );
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractBackendErrorMessage(error: HttpErrorResponse): ExtractedError {
  let message = 'An unexpected error occurred';
  let errorCode: string | undefined;

  if (error.error instanceof Error) {
    message = error.message;
    errorCode = 'NETWORK_ERROR';
  } else if (typeof error.error === 'string') {
    try {
      const parsed = JSON.parse(error.error) as BackendError;
      message = parsed.message || parsed.error || parsed.detail || error.error;
      errorCode = parsed.errorCode;
    } catch {
      message = error.error;
    }
  } else if (error.error && typeof error.error === 'object') {
    const backendError = error.error as BackendError;
    message = backendError.message || backendError.error || backendError.detail || message;
    errorCode = backendError.errorCode;
    if (backendError.errors && backendError.errors.length > 0) {
      message += ': ' + backendError.errors.join(', ');
    }
  } else {
    message = `Server error (${error.status})`;
    errorCode = `HTTP_${error.status}`;
  }

  return { message, errorCode };
}

function shouldShowToastForRequest(url: string, status: number): boolean {
  const silentPatterns = [
    /\/notifications\/unread-count$/i,
    /\/analytics\//i,
    /\/search\//i,
    /\/autocomplete\//i,
    /\/suggestions?\//i,
  ];

  const userInitiatedPatterns = [/\/apply$/i, /\/save$/i, /\/favorite$/i, /\/messages\/.+\/send$/i];

  if (silentPatterns.some((pattern) => pattern.test(url))) {
    return false;
  }

  if (userInitiatedPatterns.some((pattern) => pattern.test(url))) {
    return true;
  }

  return status >= 500 || status === 403 || status === 422;
}

function createHttpError(error: HttpErrorResponse, extracted: ExtractedError): HttpErrorResponse {
  const httpError = new HttpErrorResponse({
    error: {
      message: extracted.message,
      errorCode: extracted.errorCode,
      status: error.status,
    },
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  });
  return httpError;
}
